import { FormField } from 'ui/formField';
import { InputField, ToggleField, RadioGroupField } from 'lib/iterable-hook-form';
import { TOGGLE_SIZES } from 'ui/toggle/constants';
import { useSelector } from 'react-redux';
import { Select } from 'ui/selectv2/Select';
import { useFormContext, useFormState } from 'react-hook-form';
import { selectOrganizationUsersAsDropdownOptions } from '../selectors';
import type { OrganizationEditFormProps, AllOrgsReduxState } from '../types';
import type { OrganizationEditFormContextType } from '../contexts/OrganizationEditFormProvider';

export const OrganizationEditForm = ({ formId, orgId, onSubmit }: OrganizationEditFormProps) => {
  const { handleSubmit, setValue, getValues } = useFormContext<OrganizationEditFormContextType>();
  const { errors } = useFormState<OrganizationEditFormContextType>();

  /**
   * The displayed UI fields differ slightly from the form data, because
   * the select dropdown for the "adminUserId" field requires a different contract
   * than what the API (and the form) expects. So we do some manual mapping here to make
   * the UX conform to the field.
   */
  const orgUsers = useSelector((state: AllOrgsReduxState) =>
    selectOrganizationUsersAsDropdownOptions(state, orgId),
  );
  const currentAdminUserId = getValues('adminUserId');
  const selectValue = orgUsers.find((user) => user.value === currentAdminUserId);

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <FormField
        errorText={errors.name?.message}
        hasError={!!errors.name?.message}
        inputId="nameFormFieldId"
        isHorizontalLayout
        labelText="Name"
      >
        <InputField
          id="nameFormFieldId"
          name="name"
          data-test="edit-organization-name-field"
          autoFocus
        />
      </FormField>
      <FormField
        errorText={errors.website?.message}
        hasError={!!errors.website?.message}
        inputId="websiteFormFieldId"
        isHorizontalLayout
        labelText="Website"
      >
        <InputField
          id="websiteFormFieldId"
          name="website"
          data-test="edit-organization-website-field"
        />
      </FormField>
      <FormField
        errorText={errors.industry?.message}
        hasError={!!errors.industry?.message}
        inputId="industryFormFieldId"
        isHorizontalLayout
        labelText="Industry"
      >
        <InputField
          id="industryFormFieldId"
          name="industry"
          data-test="edit-organization-industry-field"
        />
      </FormField>
      <FormField
        errorText={errors.estimatedSize?.message}
        hasError={!!errors.estimatedSize?.message}
        inputId="estimatedSizeFormFieldId"
        isHorizontalLayout
        labelText="Estimated Size"
      >
        <InputField
          data-test="edit-organization-estimatedSize-field"
          id="estimatedSizeFormFieldId"
          name="estimatedSize"
          type="number"
          valueAsNumber
        />
      </FormField>
      <FormField
        errorText={errors.adminUserId?.message}
        hasError={!!errors.adminUserId?.message}
        inputId="adminUserFormFieldId"
        isHorizontalLayout
        labelText="Owner"
      >
        <Select
          usePortal
          data-test="edit-organization-adminUserId-field"
          id="adminUserFormFieldId"
          onSelect={([newAdmin]) =>
            // the form data mandates that the `adminUserId` field be a string (the user's id.)
            setValue('adminUserId', newAdmin.value, { shouldValidate: true, shouldTouch: true })
          }
          options={orgUsers}
          value={[selectValue]}
        />
      </FormField>
      <FormField
        errorText={errors.isEnabled?.message}
        hasError={!!errors.isEnabled?.message}
        inputId="isEnabledFormFieldId"
        isHorizontalLayout
        labelText="Enabled"
      >
        <ToggleField
          id="isEnabledFormFieldId"
          size={TOGGLE_SIZES.LARGE}
          name="isEnabled"
          data-test="edit-organization-isEnabled-field"
        />
      </FormField>
      <FormField
        errorText={errors.isPayThroughApp?.message}
        hasError={!!errors.isPayThroughApp?.message}
        inputId="isPayThroughAppFormFieldId"
        isHorizontalLayout
        labelText="Pay Thru App"
      >
        <ToggleField
          data-test="edit-organization-isPayThroughApp-field"
          id="isPayThroughAppFormFieldId"
          name="isPayThroughApp"
          size={TOGGLE_SIZES.LARGE}
        />
      </FormField>
      <FormField
        errorText={errors.showLiveChat?.message}
        hasError={!!errors.showLiveChat?.message}
        inputId="showLiveChatFormFieldId"
        isHorizontalLayout
        labelText="Chat Support"
      >
        <ToggleField
          data-test="edit-organization-showLiveChat-field"
          id="showLiveChatFormFieldId"
          name="showLiveChat"
          size={TOGGLE_SIZES.LARGE}
        />
      </FormField>
      <FormField
        errorText={errors.dataPolicy?.message}
        hasError={!!errors.dataPolicy?.message}
        inputId="dataPolicyFormFieldId"
        isHorizontalLayout
        labelText="Data Policy"
      >
        <RadioGroupField
          name="dataPolicy"
          options={[
            { id: '1', label: 'HIPAA', value: 'Hipaa' },
            { id: '2', label: 'Unrestricted', value: 'Unrestricted' },
          ]}
        />
      </FormField>
    </form>
  );
};
